/**
 * Browser tab title writer — parity with Angular `AppService.title(title, exact)`,
 * which sets `"<title> | <appName>"` (or the exact string when `exact` is true).
 *
 * Two sources feed the tab title:
 *  - the route title map (`PageTitleManager`), applied on every navigation
 *  - an optional per-page override (`usePageTitle`) for titles only the page knows
 *
 * Overrides are keyed by pathname so the outcome does not depend on whether the
 * page effect or the route effect runs first: an override only wins while its
 * pathname is the active one.
 */

import type { TitleEntry } from './types'

export const APP_NAME = import.meta.env.VITE_APP_NAME || 'Liberty Rent'

let routeEntry: TitleEntry | null = null
let overrideEntry: TitleEntry | null = null

export function formatDocumentTitle(title: string | null, exact = false): string {
  const trimmed = title?.trim() ?? ''
  if (!trimmed) return APP_NAME
  return exact ? trimmed : `${trimmed} | ${APP_NAME}`
}

function flush() {
  if (!routeEntry) return
  const entry =
    overrideEntry && overrideEntry.path === routeEntry.path ? overrideEntry : routeEntry
  document.title = formatDocumentTitle(entry.title, entry.exact)
}

/** Route-derived title for `path`; `null` falls back to the bare app name. */
export function setRouteDocumentTitle(path: string, title: string | null) {
  routeEntry = { path, title, exact: false }
  flush()
}

/** Page-supplied title that wins over the route title while `path` is active. */
export function setDocumentTitleOverride(path: string, title: string, exact: boolean) {
  overrideEntry = { path, title, exact }
  flush()
}

export function clearDocumentTitleOverride(path: string) {
  if (overrideEntry?.path !== path) return
  overrideEntry = null
  flush()
}
